import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { headerData } from "../../data/headerCostum";
import { useNavigate, useLocation } from "react-router-dom";
import SweetAlert2 from "react-sweetalert2";
import { useLocalStorage } from "../../auth/useLocalStorage";
import SecurityIcon from "@mui/icons-material/Security";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import TvIcon from "@mui/icons-material/Tv";
import StorageIcon from "@mui/icons-material/Storage";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="darkslategray"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <a
        color="inherit"
        href="https://diskominfo.makassarkota.go.id/"
        style={{ color: "inherit", textDecoration: "none" }}
      >
        Diskominfo Kota Makassar
      </a>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#e91e63',
    },
    secondary: {
      main: '#f48fb1',
    },
  },
  typography: {
    h5: {
      fontWeight: 600,
    },
  },
});

const MonitoringCCTV = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useLocalStorage("user", null);

  const [swalProps, setSwalProps] = useState({});
  const [loading, setLoading] = useState(false);

  // Get data from navigation state
  const dataLongwis = location.state?.data_longwis || null;

  const [formData, setFormData] = useState({
    status: "",
    ip_address: "",
    keterangan: "",
    foto_cctv: null,
    foto_layar: null,
    foto_nvr: null,
  });

  const [previewImages, setPreviewImages] = useState({
    foto_cctv: null,
    foto_layar: null,
    foto_nvr: null,
  });

  const statusOptions = [
    { value: "Normal", label: "Normal" },
    { value: "Rusak", label: "Rusak" },
    { value: "Maintenance", label: "Maintenance" },
    { value: "Offline", label: "Offline" },
  ];

  useEffect(() => {
    if (!dataLongwis) {
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: "Data longwis tidak ditemukan. Silakan pilih lokasi terlebih dahulu.",
        confirmButtonText: "Kembali",
        didClose: () => {
          navigate("/pic/daftar-longwis-monitoring-cctv");
        },
      });
    }
  }, [dataLongwis, navigate]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    const file = files[0];
    
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSwalProps({
          show: true,
          title: "Error",
          icon: "error",
          text: "Ukuran file terlalu besar. Maksimal 5MB.",
        });
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setSwalProps({
          show: true,
          title: "Error",
          icon: "error",
          text: "Format file tidak didukung. Gunakan JPG, JPEG, atau PNG.",
        });
        return;
      }

      setFormData(prev => ({
        ...prev,
        [name]: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImages(prev => ({
          ...prev,
          [name]: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!dataLongwis) {
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: "Data longwis tidak ditemukan.",
      });
      return;
    }

    // Validation
    if (!formData.status) {
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: "Status CCTV harus diisi.",
      });
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("status", formData.status);
      formDataToSend.append("ip_address", formData.ip_address);
      formDataToSend.append("keterangan", formData.keterangan);
      formDataToSend.append("id_user", user?.id || "");
      formDataToSend.append("id_longwis", dataLongwis.id);

      // Append files if they exist
      if (formData.foto_cctv) {
        formDataToSend.append("foto_cctv", formData.foto_cctv);
      }
      if (formData.foto_layar) {
        formDataToSend.append("foto_layar", formData.foto_layar);
      }
      if (formData.foto_nvr) {
        formDataToSend.append("foto_nvr", formData.foto_nvr);
      }

      const response = await fetch(
        process.env.REACT_APP_API_URL + "api/monitoring-cctv",
        {
          method: "POST",
          headers: {
            'Authorization': headerData.Authorization
          },
          body: formDataToSend,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setSwalProps({
        show: true,
        title: "Berhasil",
        icon: "success",
        text: "Data monitoring CCTV berhasil disimpan.",
        confirmButtonText: "OK",
        didClose: () => {
          navigate("/pic/pilihan_layanan");
        },
      });

    } catch (error) {
      console.error("Error submitting monitoring CCTV:", error);
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: error.message || "Terjadi kesalahan saat menyimpan data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const FileUploadCard = ({ name, label, icon, description }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography variant="h6" sx={{ ml: 1 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id={`${name}-upload`}
          type="file"
          name={name}
          onChange={handleFileChange}
        />
        <label htmlFor={`${name}-upload`}>
          <Button
            variant="outlined"
            component="span"
            startIcon={<PhotoCameraIcon />}
            fullWidth
            sx={{
              borderColor: '#e91e63',
              color: '#e91e63',
              '&:hover': {
                borderColor: '#c2185b',
                backgroundColor: 'rgba(233, 30, 99, 0.04)'
              }
            }}
          >
            Pilih Foto
          </Button>
        </label>
        {formData[name] && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              File: {formData[name].name}
            </Typography>
          </Box>
        )}
        {previewImages[name] && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <img
              src={previewImages[name]}
              alt={`Preview ${label}`}
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (!dataLongwis) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e91e63 0%, #f48fb1 100%)',
        py: 3
      }}
    >
      <ThemeProvider theme={defaultTheme}>
        <Container component="main" maxWidth="lg">
          <CssBaseline />
          <SweetAlert2
            {...swalProps}
            didClose={() => {
              setSwalProps({});
            }}
          />

          <Paper
            elevation={10}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#e91e63',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: 3
              }}
            >
              <SecurityIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>

            <Typography component="h1" variant="h5" sx={{ mb: 1, color: '#e91e63' }}>
              Monitoring CCTV
            </Typography>

            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
              Form monitoring kondisi CCTV Longwis
            </Typography>

            {/* Location Info */}
            <Card sx={{ width: '100%', mb: 3, bgcolor: '#e91e63', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Informasi Lokasi
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Kecamatan:</strong> {dataLongwis.kecamatan}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Kelurahan:</strong> {dataLongwis.kelurahan}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Nama Longwis:</strong> {dataLongwis.nama_longwis}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Alamat:</strong> {dataLongwis.alamat}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>PIC:</strong> {dataLongwis.nama_pic} ({dataLongwis.kontak_pic})
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <Grid container spacing={3}>
                {/* Left Column - Form Fields */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 3, color: '#e91e63' }}>
                        Data Monitoring
                      </Typography>

                      <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Status CCTV *</InputLabel>
                        <Select
                          name="status"
                          value={formData.status}
                          label="Status CCTV *"
                          onChange={handleInputChange}
                        >
                          {statusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        name="ip_address"
                        label="IP Address"
                        value={formData.ip_address}
                        onChange={handleInputChange}
                        placeholder="Contoh: 192.168.1.100"
                        sx={{ mb: 3 }}
                      />

                      <TextField
                        fullWidth
                        name="keterangan"
                        label="Keterangan"
                        multiline
                        rows={4}
                        value={formData.keterangan}
                        onChange={handleInputChange}
                        placeholder="Catatan kondisi CCTV, masalah yang ditemukan, dll."
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Right Column - File Uploads */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#e91e63' }}>
                    Dokumentasi Foto
                  </Typography>

                  <FileUploadCard
                    name="foto_cctv"
                    label="Foto CCTV"
                    icon={<SecurityIcon color="primary" />}
                    description="Foto perangkat CCTV secara keseluruhan"
                  />

                  <FileUploadCard
                    name="foto_layar"
                    label="Foto Layar Monitor"
                    icon={<TvIcon color="primary" />}
                    description="Foto tampilan layar monitoring CCTV"
                  />

                  <FileUploadCard
                    name="foto_nvr"
                    label="Foto NVR/DVR"
                    icon={<StorageIcon color="primary" />}
                    description="Foto perangkat perekam (NVR/DVR)"
                  />
                </Grid>
              </Grid>

              {/* Submit Button */}
              <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/pic/daftar-longwis-monitoring-cctv")}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderColor: '#e91e63',
                    color: '#e91e63',
                    '&:hover': {
                      borderColor: '#c2185b',
                      backgroundColor: 'rgba(233, 30, 99, 0.04)'
                    }
                  }}
                >
                  Kembali
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(90deg, #e91e63, #f48fb1)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(90deg, #c2185b, #f06292)',
                    }
                  }}
                >
                  {loading ? 'Menyimpan...' : 'Simpan Data Monitoring'}
                </Button>
              </Box>
            </Box>

            <Copyright sx={{ mt: 4 }} />
          </Paper>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default MonitoringCCTV;
